import { Competitor, CompetitorBucket } from '../../constants/competitorLeasePolicies';
import { FaqGroupId } from '../../pages/dashboard/components/marketplace/faq/faqContent';
import {
  EquityTier,
  GateDecision,
  GateVariant,
  ParkedPath,
} from '../../pages/dashboard/components/marketplace/guidance';
import { ListingStatus } from '../../pages/dashboard/components/marketplace/listingStatus';
import {
  ConversationTarget,
  initiatedOrReplied,
  negotiationState,
  targetLabel,
} from '../../pages/dashboard/components/marketplace/live/conversation/utils';
import { ContactMessageEntry } from '../marketplace/generated/kubb/models/ContactMessageEntry';
import hotjar, { HotjarEvents } from './hotjar';
import { rudderanalytics } from './rudderstack';

export enum MarketplaceSubEvent {
  // Sell flow (getting started, before the dashboard)
  TradeInPreferenceSelected = 'trade_in_preference_selected',
  TimelineSelected = 'timeline_selected',
  TransitionViewed = 'transition_viewed',

  ExplainerVideoShown = 'explainer_video_shown',
  ExplainerVideoPlayed = 'explainer_video_played',
  ExplainerVideoProgress = 'explainer_video_progress',
  ExplainerVideoCompleted = 'explainer_video_completed',
  ExplainerVideoClosed = 'explainer_video_closed',
  ExplainerVideoCollapsed = 'explainer_video_collapsed',
  ExplainerVideoExpanded = 'explainer_video_expanded',

  // Dashboard visits
  DashboardLanded = 'dashboard_landed',
  DashboardReturned = 'dashboard_returned',
  FaqOpened = 'faq_opened',
  FaqQuestionExpanded = 'faq_question_expanded',

  // Prelist checklist (condition report, photos, asking price)
  ChecklistItemTapped = 'checklist_item_tapped',
  ChecklistCompleted = 'checklist_completed',
  ConditionReportStarted = 'condition_report_started',
  ConditionReportSectionDone = 'condition_report_section_done',
  ConditionReportCompleted = 'condition_report_completed',
  PhotoCaptureStarted = 'photo_capture_started',
  PhotoUploaded = 'photo_uploaded',
  PhotosCompleted = 'photos_completed',
  AskingPriceUnlocked = 'asking_price_unlocked',
  AskingPriceSet = 'asking_price_set',
  ListingActivated = 'listing_activated',

  // Payoff collection
  PayoffCardSubmitTapped = 'payoff_card_submit_tapped',
  PayoffSelfServeLinkTapped = 'payoff_self_serve_link_tapped',
  PayoffSelfServeModalSubmitted = 'payoff_self_serve_modal_submitted',
  PayoffSelfServeModalDismissed = 'payoff_self_serve_modal_dismissed',

  // Path B marketplace intake modal (MarketplaceIntakeModal)
  PathBModalShown = 'path_b_modal_shown',
  PathBTradeInSelected = 'path_b_trade_in_selected',
  PathBModalCompleted = 'path_b_modal_completed',

  // Pre-activation gate (deep-negative tiers pick a path)
  GateViewed = 'gate_viewed',
  GateDecisionClicked = 'gate_decision_clicked',
  GateEditDetailsOpened = 'gate_edit_details_opened',

  // Waiting state (no offers yet): tier lanes + keep-the-car buyout overlay
  WaitingLaneViewed = 'waiting_lane_viewed',
  BuyoutOverlayOpened = 'buyout_overlay_opened',
  BuyoutCallClicked = 'buyout_call_clicked',
  CompetitorPolicyOpened = 'competitor_policy_opened',
  CompetitorPolicyLinkClicked = 'competitor_policy_link_clicked',

  ParkedViewed = 'parked_viewed',
  ParkedRelistClicked = 'parked_relist_clicked',

  // Offer dashboard (driver actions on live offers)
  OfferDashboardViewed = 'offer_dashboard_viewed',
  OfferAcceptModalOpened = 'offer_accept_modal_opened',
  OfferAcceptConfirmed = 'offer_accept_confirmed',
  OfferCounterModalOpened = 'offer_counter_modal_opened',
  OfferCounterSubmitted = 'offer_counter_submitted',
  OfferAdjustModalOpened = 'offer_adjust_modal_opened',
  OfferAdjustSubmitted = 'offer_adjust_submitted',
  OfferWithdrawModalOpened = 'offer_withdraw_modal_opened',
  CounterWithdrawConfirmed = 'counter_withdraw_confirmed',
  OfferActionFailed = 'offer_action_failed',

  // Dealer messaging
  ConversationOpened = 'conversation_opened',
  MessageSent = 'message_sent',
  MessageSendFailed = 'message_send_failed',

  // Post-acceptance hub (finish the sale)
  PostAcceptanceHubViewed = 'post_acceptance_hub_viewed',
  PostAcceptanceRevealViewed = 'post_acceptance_reveal_viewed',
  PostAcceptanceRevealFinishTapped = 'post_acceptance_reveal_finish_tapped',
  StageAdvanced = 'stage_advanced',
  DocumentUploaded = 'document_uploaded',
  DocumentUploadFailed = 'document_upload_failed',
}

export type TradeInPreference = 'open_to_trade_in' | 'just_sell' | 'not_sure';
export type ExplainerVideoContext =
  | { placement: 'sell_details'; tempInfoId: string }
  | { placement: 'condition_report'; dealId: string };
type ChecklistItem = 'condition_report' | 'photos' | 'asking_price';
type ChecklistItemStatus = 'incomplete' | 'complete' | 'locked';

const track = (subEvent: MarketplaceSubEvent, props: Record<string, unknown>) => {
  try {
    rudderanalytics.track('marketplace', { sub_event: subEvent, ...props });
  } catch {
    // Fire-and-forget: analytics must never break a user action.
  }
};

// Sell flow (getting started) — prefixed because these are called from outside the marketplace dashboard

export const trackMarketplaceTradeInSelected = (
  tempInfoId: string,
  tradeInPreference: TradeInPreference,
) => track(MarketplaceSubEvent.TradeInPreferenceSelected, { tempInfoId, tradeInPreference });

export const trackMarketplaceTimelineSelected = (tempInfoId: string, sellTimeline: string) =>
  track(MarketplaceSubEvent.TimelineSelected, { tempInfoId, sellTimeline });

export const trackMarketplaceTransitionViewed = (tempInfoId: string, dealId: string) =>
  track(MarketplaceSubEvent.TransitionViewed, { tempInfoId, dealId });

export const trackExplainerVideoShown = (context: ExplainerVideoContext) =>
  track(MarketplaceSubEvent.ExplainerVideoShown, { ...context });

export const trackExplainerVideoPlayed = (context: ExplainerVideoContext) =>
  track(MarketplaceSubEvent.ExplainerVideoPlayed, { ...context });

export const trackExplainerVideoProgress = (
  context: ExplainerVideoContext,
  percent: 25 | 50 | 75,
) => track(MarketplaceSubEvent.ExplainerVideoProgress, { ...context, percent });

export const trackExplainerVideoCompleted = (context: ExplainerVideoContext) =>
  track(MarketplaceSubEvent.ExplainerVideoCompleted, { ...context });

export const trackExplainerVideoClosed = (context: ExplainerVideoContext, watchedSeconds: number) =>
  track(MarketplaceSubEvent.ExplainerVideoClosed, { ...context, watchedSeconds });

export const trackExplainerVideoCollapsed = (context: ExplainerVideoContext) =>
  track(MarketplaceSubEvent.ExplainerVideoCollapsed, { ...context });

export const trackExplainerVideoExpanded = (context: ExplainerVideoContext) =>
  track(MarketplaceSubEvent.ExplainerVideoExpanded, { ...context });

// Dashboard

// Hotjar merges identify attributes per user, so this only needs the marketplace delta;
// RootDashboard already sends deal_id and state under the same vin.
export const identifyMarketplaceUser = (props: {
  vin: string;
  leadStatus: ListingStatus;
  offerAccepted: boolean;
}) =>
  hotjar.identify(props.vin, {
    lead_status: props.leadStatus,
    offer_accepted: props.offerAccepted,
  });

export const trackDashboardLanded = (props: {
  dealId: string;
  listingStatus: string;
  itemsComplete: number;
}) => {
  track(MarketplaceSubEvent.DashboardLanded, props);
  hotjar.track(HotjarEvents.MarketplaceDashboard);
};

export const trackDashboardReturned = (props: {
  dealId: string;
  listingStatus: string;
  itemsComplete: number;
}) => track(MarketplaceSubEvent.DashboardReturned, props);

export const trackChecklistItemTapped = (
  dealId: string,
  item: ChecklistItem,
  itemStatus: ChecklistItemStatus,
) => track(MarketplaceSubEvent.ChecklistItemTapped, { dealId, item, itemStatus });

export const trackAskingPriceUnlocked = (dealId: string) =>
  track(MarketplaceSubEvent.AskingPriceUnlocked, { dealId });

export const trackAskingPriceSet = (dealId: string, askingPrice: number) =>
  track(MarketplaceSubEvent.AskingPriceSet, { dealId, askingPrice });

export const trackListingActivated = (dealId: string, askingPrice?: number) => {
  track(MarketplaceSubEvent.ListingActivated, { dealId, askingPrice: askingPrice ?? null });
  hotjar.track(HotjarEvents.MarketplaceListingActivated);
};

// Condition report

export const trackConditionReportStarted = (dealId: string) =>
  track(MarketplaceSubEvent.ConditionReportStarted, { dealId });

export const trackConditionReportSectionDone = (dealId: string, section: string) =>
  track(MarketplaceSubEvent.ConditionReportSectionDone, { dealId, section });

export const trackConditionReportCompleted = (dealId: string) =>
  track(MarketplaceSubEvent.ConditionReportCompleted, { dealId });

// Photos

export const trackPhotoCaptureStarted = (dealId: string) =>
  track(MarketplaceSubEvent.PhotoCaptureStarted, { dealId });

export const trackPhotoUploaded = (
  dealId: string,
  photoSlot: string,
  category: string,
  photoNumber: number,
) => track(MarketplaceSubEvent.PhotoUploaded, { dealId, photoSlot, category, photoNumber });

export const trackPhotosCompleted = (dealId: string) =>
  track(MarketplaceSubEvent.PhotosCompleted, { dealId });

// Documents (post-acceptance)

export const trackDocumentUploaded = (dealId: string, documentType: string, leadId?: string) =>
  track(MarketplaceSubEvent.DocumentUploaded, {
    deal_id: dealId,
    document_type: documentType,
    lead_id: leadId,
  });

// Reason is rejected client-side (file_too_large, unsupported_type) or a failed
// request (upload_failed). No leadId: the upload never returned one on failure.
export const trackDocumentUploadFailed = (
  dealId: string,
  documentType: string,
  reason: 'file_too_large' | 'unsupported_type' | 'upload_failed',
) =>
  track(MarketplaceSubEvent.DocumentUploadFailed, {
    deal_id: dealId,
    document_type: documentType,
    reason,
  });

// Payoff card

export const trackPayoffCardSubmitTapped = (dealId: string, missingFields: string[]) =>
  track(MarketplaceSubEvent.PayoffCardSubmitTapped, { dealId, missingFields });

export const trackPayoffSelfServeLinkTapped = (dealId: string) =>
  track(MarketplaceSubEvent.PayoffSelfServeLinkTapped, { dealId });

export const trackPayoffSelfServeModalSubmitted = (dealId: string, vehiclePayoff: number) =>
  track(MarketplaceSubEvent.PayoffSelfServeModalSubmitted, { dealId, vehiclePayoff });

export const trackPayoffSelfServeModalDismissed = (dealId: string) =>
  track(MarketplaceSubEvent.PayoffSelfServeModalDismissed, { dealId });

// Path B modal

export const trackPathBModalShown = (dealId: string) =>
  track(MarketplaceSubEvent.PathBModalShown, { dealId });

export const trackPathBTradeInSelected = (dealId: string, tradeInPreference: TradeInPreference) =>
  track(MarketplaceSubEvent.PathBTradeInSelected, { dealId, tradeInPreference });

export const trackPathBModalCompleted = (
  dealId: string,
  tradeInPreference: TradeInPreference,
  sellTimeline: string,
) => track(MarketplaceSubEvent.PathBModalCompleted, { dealId, tradeInPreference, sellTimeline });

// Offer dashboard (driver actions) — properties are snake_cased to match the agreed Hex schema.

export type OfferActionType = 'accept' | 'counter' | 'adjust' | 'withdraw';

// Where the driver initiated the action: an offer card on the dashboard, or the current offer's
// mirror card inside the conversation thread.
export type OfferActionSurface = 'dashboard' | 'thread';

export type OfferDashboardSubEvent =
  | MarketplaceSubEvent.OfferDashboardViewed
  | MarketplaceSubEvent.OfferAcceptModalOpened
  | MarketplaceSubEvent.OfferAcceptConfirmed
  | MarketplaceSubEvent.OfferCounterModalOpened
  | MarketplaceSubEvent.OfferCounterSubmitted
  | MarketplaceSubEvent.OfferAdjustModalOpened
  | MarketplaceSubEvent.OfferAdjustSubmitted
  | MarketplaceSubEvent.OfferWithdrawModalOpened
  | MarketplaceSubEvent.CounterWithdrawConfirmed
  | MarketplaceSubEvent.OfferActionFailed;

export type OfferDashboardEventProps = {
  leadId: string;
  offerId?: string;
  dealerLabel?: string;
  dealerAmount?: number;
  driverAmount?: number;
  secondsSinceDashboardView?: number;
  secondsSinceOfferArrived?: number;
  pendingOfferCount?: number;
  pendingCounterCount?: number;
  actionType?: OfferActionType;
  surface?: OfferActionSurface;
};

export const trackOfferDashboardEvent = (
  subEvent: OfferDashboardSubEvent,
  props: OfferDashboardEventProps,
) => {
  const payload: Record<string, unknown> = { lead_id: props.leadId };
  if (props.offerId != null) payload.offer_id = props.offerId;
  if (props.dealerLabel != null) payload.dealer_label = props.dealerLabel;
  if (props.dealerAmount != null) payload.dealer_amount = props.dealerAmount;
  if (props.driverAmount != null) payload.driver_amount = props.driverAmount;
  if (props.secondsSinceDashboardView != null)
    payload.seconds_since_dashboard_view = props.secondsSinceDashboardView;
  if (props.secondsSinceOfferArrived != null)
    payload.seconds_since_offer_arrived = props.secondsSinceOfferArrived;
  if (props.pendingOfferCount != null) payload.pending_offer_count = props.pendingOfferCount;
  if (props.pendingCounterCount != null) payload.pending_counter_count = props.pendingCounterCount;
  if (props.actionType != null) payload.action_type = props.actionType;
  if (props.surface != null) payload.surface = props.surface;

  track(subEvent, payload);
  if (subEvent === MarketplaceSubEvent.OfferAcceptConfirmed) {
    hotjar.track(HotjarEvents.MarketplaceOfferAccepted);
  }
};

// Messaging — same masked dealer_label as the offer events so messaging joins to the winning dealer.
const messagingEventBase = (leadId: string, target: ConversationTarget) => ({
  lead_id: leadId,
  dealer_label: targetLabel(target),
  negotiation_state: negotiationState(target),
});

export const trackConversationOpened = (leadId: string, target: ConversationTarget) =>
  track(MarketplaceSubEvent.ConversationOpened, messagingEventBase(leadId, target));

export const trackMessageSent = (
  leadId: string,
  target: ConversationTarget,
  body: string,
  thread: ContactMessageEntry[] | undefined,
) =>
  track(MarketplaceSubEvent.MessageSent, {
    ...messagingEventBase(leadId, target),
    message_length: body.length,
    initiated_or_replied: initiatedOrReplied(thread),
  });

export const trackMessageSendFailed = (leadId: string, target: ConversationTarget, body: string) =>
  track(MarketplaceSubEvent.MessageSendFailed, {
    ...messagingEventBase(leadId, target),
    message_length: body.length,
  });

// Post-acceptance hub. LE-18121 owns the full event set; LE-17798 fires the hub-viewed trigger.

export const trackPostAcceptanceHubViewed = (props: {
  dealId: string;
  leadId: string;
  leadStatus: string;
}) =>
  track(MarketplaceSubEvent.PostAcceptanceHubViewed, {
    deal_id: props.dealId,
    lead_id: props.leadId,
    lead_status: props.leadStatus,
  });

export const trackChecklistCompleted = (props: { dealId: string; leadId: string }) => {
  track(MarketplaceSubEvent.ChecklistCompleted, { deal_id: props.dealId, lead_id: props.leadId });
  hotjar.track(HotjarEvents.MarketplaceChecklistCompleted);
};

export const trackStageAdvanced = (props: {
  dealId: string;
  leadId: string;
  fromStage: string;
  toStage: string;
}) =>
  track(MarketplaceSubEvent.StageAdvanced, {
    deal_id: props.dealId,
    lead_id: props.leadId,
    from_stage: props.fromStage,
    to_stage: props.toStage,
  });

type RevealEventProps = {
  dealId: string;
  leadId: string;
  leadStatus: string;
  isCounterAccepted: boolean;
};

const trackRevealEvent = (subEvent: MarketplaceSubEvent, props: RevealEventProps) =>
  track(subEvent, {
    deal_id: props.dealId,
    lead_id: props.leadId,
    lead_status: props.leadStatus,
    accepted_via: props.isCounterAccepted ? 'counter' : 'driver',
  });

export const trackSoldRevealViewed = (props: RevealEventProps) =>
  trackRevealEvent(MarketplaceSubEvent.PostAcceptanceRevealViewed, props);

export const trackSoldRevealFinishTapped = (props: RevealEventProps) =>
  trackRevealEvent(MarketplaceSubEvent.PostAcceptanceRevealFinishTapped, props);

// Waiting-state tier lanes

export type BuyoutOverlaySource =
  | 'lane'
  | 'keep_card'
  | 'gate_two_way'
  | 'gate_three_way'
  | 'parked_buyout'
  | 'parked_finish_lease';

export const trackWaitingLaneViewed = (dealId: string, leadId: string, tier: EquityTier) =>
  track(MarketplaceSubEvent.WaitingLaneViewed, { deal_id: dealId, lead_id: leadId, tier });

export const trackBuyoutOverlayOpened = (
  dealId: string,
  leadId: string,
  source: BuyoutOverlaySource,
) => track(MarketplaceSubEvent.BuyoutOverlayOpened, { deal_id: dealId, lead_id: leadId, source });

export const trackBuyoutCallClicked = (
  dealId: string,
  leadId: string,
  source: BuyoutOverlaySource,
) => track(MarketplaceSubEvent.BuyoutCallClicked, { deal_id: dealId, lead_id: leadId, source });

export const trackCompetitorPolicyOpened = (
  dealId: string,
  leadId: string,
  bucket: CompetitorBucket,
) =>
  track(MarketplaceSubEvent.CompetitorPolicyOpened, { deal_id: dealId, lead_id: leadId, bucket });

export const trackCompetitorPolicyLinkClicked = (
  dealId: string,
  leadId: string,
  bucket: CompetitorBucket,
  competitor: Competitor,
) =>
  track(MarketplaceSubEvent.CompetitorPolicyLinkClicked, {
    deal_id: dealId,
    lead_id: leadId,
    bucket,
    competitor,
  });

// Pre-activation gate

export const trackGateViewed = (dealId: string, leadId: string, variant: GateVariant) =>
  track(MarketplaceSubEvent.GateViewed, { deal_id: dealId, lead_id: leadId, variant });

export const trackGateDecisionClicked = (
  dealId: string,
  leadId: string,
  variant: GateVariant,
  decision: GateDecision,
) =>
  track(MarketplaceSubEvent.GateDecisionClicked, {
    deal_id: dealId,
    lead_id: leadId,
    variant,
    decision,
  });

export const trackGateEditDetailsOpened = (dealId: string, leadId: string) =>
  track(MarketplaceSubEvent.GateEditDetailsOpened, { deal_id: dealId, lead_id: leadId });

export const trackParkedViewed = (dealId: string, leadId: string, path: ParkedPath) =>
  track(MarketplaceSubEvent.ParkedViewed, { deal_id: dealId, lead_id: leadId, path });

export const trackParkedRelistClicked = (dealId: string, leadId: string, path: ParkedPath) =>
  track(MarketplaceSubEvent.ParkedRelistClicked, { deal_id: dealId, lead_id: leadId, path });

// FAQ drawer

export const trackFaqOpened = (dealId: string, listingStatus: ListingStatus, isMobile: boolean) =>
  track(MarketplaceSubEvent.FaqOpened, {
    deal_id: dealId,
    listing_status: listingStatus,
    is_mobile: isMobile,
  });

export const trackFaqQuestionExpanded = (
  dealId: string,
  listingStatus: ListingStatus,
  questionId: string,
  group: FaqGroupId,
) =>
  track(MarketplaceSubEvent.FaqQuestionExpanded, {
    deal_id: dealId,
    listing_status: listingStatus,
    question_id: questionId,
    group,
  });
